import { Link, createFileRoute } from '@tanstack/react-router';
import { Compass, EyeOff, Plus, Search, Users } from 'lucide-react';

import { EmptyState } from '@/components/EmptyState';
import { EMPTY_ACTION_CLASS, EMPTY_SECONDARY_CLASS } from '@/components/emptyStateClasses';
import { PageHeader } from '@/components/PageHeader';
import { Chip } from '@/components/exercise/Chip';
import { CommunityAside } from '@/components/community/CommunityAside';
import { PostFeed } from '@/components/community/PostFeed';
import { POST_TYPE_FILTERS } from '@/components/community/format';
import { communityFeedSchema } from '@/components/community/searchParams';
import { useAuth } from '@/lib/auth';
import { useExploreFeed, useHomeFeed } from '@/lib/queries/community';

export const Route = createFileRoute('/_app/community/')({
  validateSearch: communityFeedSchema,
  component: CommunityPage,
});

/**
 * Pestaña Comunidad: el muro de a quién sigues y el de descubrimiento.
 *
 * Las dos consultas viven a la vez —montadas, no destruidas al cambiar de
 * pestaña— para que volver a «Siguiendo» no vuelva a pedir el muro entero. Lo
 * que decide cuál se ve es el parámetro `tab`, que va en la URL para poder
 * compartir «mira esto en explorar».
 */
function CommunityPage() {
  const { tab, type, sort = 'recent' } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  const exploring = tab === 'explore';

  // Sólo se pide el muro que se está viendo, pero los dos siguen montados: al
  // volver a la otra pestaña la caché ya está caliente y no parpadea.
  const home = useHomeFeed(!exploring);
  const explore = useExploreFeed(type, exploring, sort);
  const query = exploring ? explore : home;
  const posts = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    // Escritorio: el muro en una columna de lectura centrada y, en `xl`, una
    // columna lateral con tu perfil y los caminos para descubrir.
    <div className="xl:grid xl:grid-cols-[minmax(0,40rem)_20rem] xl:justify-center xl:gap-8">
      <div className="mx-auto w-full max-w-[40rem] min-w-0 xl:mx-0">
        <PageHeader
          title="Comunidad"
          subtitle="Lo que comparte la gente a la que sigues."
          action={
            <Link
              to="/community/search"
              search={{}}
              aria-label="Buscar cuentas"
              className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
            >
              <Search className="size-5" aria-hidden="true" />
            </Link>
          }
        />

        {user !== null && !user.isPublic ? (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-line bg-surface-raised p-3">
            <EyeOff className="mt-0.5 size-4 shrink-0 text-ink-muted" aria-hidden="true" />
            <p className="min-w-0 flex-1 text-sm text-ink-muted">
              Tu perfil es privado: los demás no verán tus publicaciones.{' '}
              <Link to="/settings/profile" className="font-semibold text-brand underline">
                Editar perfil
              </Link>
            </p>
          </div>
        ) : null}

        <div
          role="tablist"
          aria-label="Muros de la comunidad"
          className="mb-3 flex gap-1 rounded-xl border border-line bg-surface-raised p-1"
        >
          <TabButton
            selected={!exploring}
            onClick={() => void navigate({ search: {}, replace: true })}
          >
            Siguiendo
          </TabButton>
          <TabButton
            selected={exploring}
            onClick={() => void navigate({ search: { tab: 'explore' }, replace: true })}
          >
            Explorar
          </TabButton>
        </div>

        {exploring ? (
          <div
            role="tablist"
            aria-label="Orden de Explorar"
            className="mb-3 flex gap-1 rounded-xl border border-line bg-surface-raised p-1"
          >
            <TabButton
              selected={sort === 'recent'}
              onClick={() =>
                void navigate({ search: { tab: 'explore', type, sort: 'recent' }, replace: true })
              }
            >
              Recientes
            </TabButton>
            <TabButton
              selected={sort === 'trending'}
              onClick={() =>
                void navigate({ search: { tab: 'explore', type, sort: 'trending' }, replace: true })
              }
            >
              Tendencias
            </TabButton>
          </div>
        ) : null}

        {exploring ? (
          <div className="-mx-4 mb-2 -mt-1 flex gap-2 overflow-x-auto px-4 py-1 xs:-mx-6 xs:px-6">
            <Chip
              selected={type === undefined}
              onClick={() => void navigate({ search: { tab: 'explore', sort }, replace: true })}
            >
              Todo
            </Chip>
            {POST_TYPE_FILTERS.map((filter) => (
              <Chip
                key={filter.value}
                selected={type === filter.value}
                onClick={() =>
                  void navigate({
                    search: {
                      tab: 'explore',
                      sort,
                      type: type === filter.value ? undefined : filter.value,
                    },
                    replace: true,
                  })
                }
              >
                {filter.label}
              </Chip>
            ))}
          </div>
        ) : null}

        <div className="pb-20">
          <PostFeed
            posts={posts}
            isPending={query.isPending}
            isError={query.isError}
            error={query.error}
            onRetry={() => void query.refetch()}
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            onLoadMore={() => void query.fetchNextPage()}
            errorTitle={
              exploring
                ? 'No hemos podido cargar el muro de descubrimiento'
                : 'No hemos podido cargar tu muro'
            }
            empty={exploring ? <ExploreEmpty /> : <FollowingEmpty />}
          />
        </div>
      </div>

      <CommunityAside className="hidden xl:sticky xl:top-10 xl:mt-10 xl:flex" />

      {/* Botón flotante: se alinea con el borde derecho del muro (40rem más el
          relleno de la página); en `xl` lo sustituye el «Publicar» de la
          columna lateral. Sin `w-full`: con `left-rail` y el ancho de la
          pantalla, la caja se salía 80 px por la derecha. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 mx-auto flex max-w-[calc(40rem+2rem)] justify-end px-4 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] xs:max-w-[calc(40rem+3rem)] xs:px-6 md:left-rail md:max-w-[calc(40rem+4rem)] md:px-8 md:pb-8 lg:left-sidebar xl:hidden">
        <Link
          to="/community/compose"
          search={{}}
          className="pointer-events-auto flex min-h-14 items-center gap-2 rounded-full bg-brand px-5 font-semibold text-brand-ink shadow-lg shadow-black/40 transition-colors hover:bg-brand-strong"
        >
          <Plus className="size-5" aria-hidden="true" />
          Publicar
        </Link>
      </div>
    </div>
  );
}

function TabButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      onClick={onClick}
      className={
        selected
          ? 'min-h-11 flex-1 rounded-lg bg-brand text-sm font-semibold text-brand-ink'
          : 'min-h-11 flex-1 rounded-lg text-sm font-medium text-ink-muted transition-colors hover:bg-surface-overlay active:bg-surface-overlay'
      }
    >
      {children}
    </button>
  );
}

function FollowingEmpty() {
  return (
    <EmptyState
      icon={Users}
      title="Aún no sigues a nadie que haya publicado"
      description="Aquí verás lo que publica la gente a la que sigues. Busca a tus amigos por su nombre o su @, o mira lo que comparte el grupo."
      action={
        <>
          <Link to="/community/search" search={{}} className={EMPTY_ACTION_CLASS}>
            <Search className="size-4" aria-hidden="true" />
            Buscar a mis amigos
          </Link>
          <Link to="/community" search={{ tab: 'explore' }} className={EMPTY_SECONDARY_CLASS}>
            <Compass className="size-4" aria-hidden="true" />
            Explorar la comunidad
          </Link>
        </>
      }
    />
  );
}

function ExploreEmpty() {
  return (
    <EmptyState
      icon={Compass}
      title="No hay nada con este filtro"
      description="Prueba con otro tipo de publicación o quita el filtro."
    />
  );
}
